// src/encounter/lib/l360-lifestyle-intake.ts
// L360 P1: the T03 "Six-domain lifestyle assessment" template, read as one state per domain for the
// `lifestyle.intake_submitted` event. Pure: no network, no React. Same design as l360-history-review.ts (I2).
// The sender is services/use-l360-lifestyle-intake-event.ts.
//
// T03's shape: a root group coded longevity360-template T03, a display guide, then one group per domain, each coded
// longevity360-field `T03.<domain>`. Inside a domain: pairs of a value item and a companion choice `<value>-state`
// (unknown | asked-declined | not-applicable). An item is every item whose linkId + "-state" also exists, so the
// domains and their items are read from the template, never listed here.
// Spec: P1 is complete when each of the six domains is answered OR explicitly unknown/declined. A blank domain stays incomplete.
import type { FhirQuestionnaire, FhirQuestionnaireItem } from '@/types/template-studio-api';
import type { QuestionnaireResponseResource } from '@/types/questionnaire-response-save-api';
import { CHIP_STATE, L360_TEMPLATE_SYSTEM, collectItems, hasValue, indexResponse, type HistorySectionState } from './l360-history-review';

export const L360_LIFESTYLE_TEMPLATE_CODE = 'T03';
const FIELD_SYSTEM = 'https://fhir.scrips.com/CodeSystem/longevity360-field';
const STATE_SUFFIX = '-state';

export type LifestyleItemState = HistorySectionState;
export interface LifestyleItem {
  id: string;
  state: LifestyleItemState;
}
export interface LifestyleDomain {
  id: string;
  state: 'complete' | 'incomplete';
  items: LifestyleItem[];
}

function findRoot(items: readonly FhirQuestionnaireItem[] | null | undefined): FhirQuestionnaireItem | null {
  for (const it of items ?? []) {
    if (it.type === 'group' && it.code?.some((c) => c.system === L360_TEMPLATE_SYSTEM && c.code === L360_LIFESTYLE_TEMPLATE_CODE)) return it;
    const inner = findRoot(it.item);
    if (inner) return inner;
  }
  return null;
}

/**
 * One entry per domain group under the T03 root, in template order. An item is `known` when a value is present (a
 * whitespace-only string is no value). Otherwise the companion chip decides: unknown, declined, not_applicable.
 * Neither: `missing`. A domain is `complete` when it has at least one item and none is `missing`.
 * A template without a T03 root gives [].
 */
export function summarizeLifestyleDomains(
  questionnaire: FhirQuestionnaire | null | undefined,
  storedQr: QuestionnaireResponseResource | null | undefined,
): LifestyleDomain[] {
  const root = findRoot(questionnaire?.item);
  if (!root) return [];
  const answers = indexResponse(storedQr?.item, new Map());
  const prefix = `${L360_LIFESTYLE_TEMPLATE_CODE}.`;
  const domains: LifestyleDomain[] = [];
  for (const group of root.item ?? []) {
    if (group.type !== 'group') continue; // the guide is a display item, not a domain
    // The id is the field code `T03.<domain>` when the group has one, else the linkId after the root's prefix.
    const field = group.code?.find((c) => c.system === FIELD_SYSTEM)?.code;
    const id = field?.startsWith(prefix) ? field.slice(prefix.length) : group.linkId.slice(root.linkId.length + 1);
    const all = collectItems(group.item, []);
    const linkIds = new Set(all.map((i) => i.linkId));
    const items: LifestyleItem[] = [];
    for (const item of all) {
      const stateId = item.linkId + STATE_SUFFIX;
      if (item.linkId.endsWith(STATE_SUFFIX) || !linkIds.has(stateId)) continue;
      // An item the template marks optional (required: false) never holds the intake open. Absent = required.
      if (item.required === false) continue;
      let state: LifestyleItemState = 'missing';
      if ((answers.get(item.linkId) ?? []).some(hasValue)) state = 'known';
      else {
        const chip = (answers.get(stateId) ?? []).map((a) => a.valueCoding?.code ?? '').find((c) => c in CHIP_STATE);
        if (chip) state = CHIP_STATE[chip];
      }
      items.push({ id: item.linkId.startsWith(group.linkId + '-') ? item.linkId.slice(group.linkId.length + 1) : item.linkId, state });
    }
    domains.push({ id, state: items.length > 0 && items.every((i) => i.state !== 'missing') ? 'complete' : 'incomplete', items });
  }
  return domains;
}

/** Spec P1: "each of six domains answered OR explicitly unknown/declined". The six T03 domain ids. */
export const LIFESTYLE_DOMAIN_IDS = ['nutrition', 'activity', 'sleep', 'stress', 'connection', 'substances'] as const;

/**
 * True only when each of the six spec domains is present AND complete, and no other domain is incomplete. A template
 * edit that drops or renames a domain stops the event (P1 stays open) rather than completing P1 with fewer domains.
 * A draft posts nothing: Workflows records P1 from the FIRST post, so an early post would move the run past P1.
 */
export function isLifestyleIntakeReady(domains: readonly LifestyleDomain[]): boolean {
  const complete = new Set(domains.filter((d) => d.state === 'complete').map((d) => d.id));
  return LIFESTYLE_DOMAIN_IDS.every((id) => complete.has(id)) && domains.every((d) => d.state === 'complete');
}

/**
 * A short deterministic string of the states (FNV-1a over "domain=state|item=state|..." in hex). A text edit inside a
 * complete intake keeps the revision; a state change (known to declined, say) changes it.
 */
export function lifestyleIntakeRevision(domains: readonly LifestyleDomain[]): string {
  const text = domains.map((d) => [`${d.id}=${d.state}`, ...d.items.map((i) => `${i.id}=${i.state}`)].join('|')).join('|');
  let h = 0x811c9dc5;
  for (let i = 0; i < text.length; i++) {
    h ^= text.charCodeAt(i);
    h = Math.imul(h, 0x01000193) >>> 0;
  }
  return h.toString(16).padStart(8, '0');
}
